import { useState, useEffect } from "react";
import { X, Send, Mail } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface Letter {
  id: string;
  content: string;
  date: string;
}

/**
 * "写一封信"弹窗组件
 * 使用 localStorage 保存留言，不涉及后端
 */
interface LetterModalProps {
  open: boolean;
  onClose: () => void;
}

export default function LetterModal({ open, onClose }: LetterModalProps) {
  const [content, setContent] = useState("");
  const [letters, setLetters] = useState<Letter[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem("travel-letters");
    if (saved) {
      try {
        setLetters(JSON.parse(saved));
      } catch { /* ignore */ }
    }
  }, [open]);

  const handleSave = () => {
    if (!content.trim()) return;
    const newLetter: Letter = {
      id: Date.now().toString(),
      content: content.trim(),
      date: new Date().toLocaleDateString("zh-CN"),
    };
    const updated = [newLetter, ...letters];
    setLetters(updated);
    localStorage.setItem("travel-letters", JSON.stringify(updated));
    setContent("");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: "oklch(0.18 0.06 45 / 0.55)" }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="w-full max-w-lg rounded-2xl overflow-hidden"
            style={{
              backgroundColor: "oklch(0.99 0.01 70)",
              boxShadow: "var(--ds-shadow-xl)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between"
              style={{
                padding: "var(--spacing-md) var(--spacing-lg)",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div className="flex items-center" style={{ gap: "var(--spacing-sm)" }}>
                <Mail className="w-5 h-5" style={{ color: "oklch(0.62 0.18 35)" }} />
                <h3
                  className="font-semibold"
                  style={{
                    fontFamily: "var(--font-handwritten)",
                    fontSize: "var(--font-size-title)",
                    color: "var(--foreground)",
                  }}
                >
                  写一封信
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-full hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />
              </button>
            </div>

            {/* Content */}
            <div className="flex flex-col" style={{ padding: "var(--spacing-lg)", gap: "var(--spacing-md)" }}>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="写下此刻想对 TA 说的话..."
                className="w-full resize-none rounded-xl border outline-none focus:ring-2 transition-all"
                style={{
                  padding: "var(--spacing-md)",
                  minHeight: 120,
                  fontFamily: "var(--font-handwritten)",
                  fontSize: "var(--font-size-body)",
                  borderColor: "var(--border)",
                  backgroundColor: "oklch(0.97 0.015 70)",
                  color: "var(--foreground)",
                  lineHeight: 1.8,
                }}
              />
              <button
                onClick={handleSave}
                disabled={!content.trim()}
                className="flex items-center justify-center rounded-xl font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  gap: "var(--spacing-sm)",
                  padding: "var(--spacing-sm) var(--spacing-lg)",
                  backgroundColor: "oklch(0.62 0.18 35)",
                  color: "white",
                  fontSize: "var(--font-size-label)",
                }}
              >
                <Send className="w-4 h-4" />
                保存到留言墙
              </button>

              {/* Saved letters */}
              {letters.length > 0 && (
                <div className="flex flex-col" style={{ gap: "var(--spacing-sm)", marginTop: "var(--spacing-sm)" }}>
                  <h4
                    style={{
                      fontSize: "var(--font-size-label)",
                      color: "var(--muted-foreground)",
                      fontWeight: 500,
                    }}
                  >
                    留言墙（{letters.length} 条）
                  </h4>
                  <div className="flex flex-col max-h-48 overflow-y-auto" style={{ gap: "var(--spacing-sm)" }}>
                    {letters.map((letter) => (
                      <motion.div
                        key={letter.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="rounded-lg"
                        style={{
                          padding: "var(--spacing-sm) var(--spacing-md)",
                          backgroundColor: "oklch(0.96 0.02 65)",
                          borderLeft: "3px solid oklch(0.62 0.18 35)",
                        }}
                      >
                        <p
                          style={{
                            fontFamily: "var(--font-handwritten)",
                            fontSize: "var(--font-size-body)",
                            color: "var(--foreground)",
                            lineHeight: 1.7,
                          }}
                        >
                          {letter.content}
                        </p>
                        <p
                          className="mt-1"
                          style={{
                            fontSize: "var(--font-size-small)",
                            color: "var(--muted-foreground)",
                          }}
                        >
                          {letter.date}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
